<script>
  import Header from './Header.svelte';
  import { slide } from 'svelte/transition';
  import Footer from './Footer.svelte';
  import Input from './Input.svelte';
  import CommentAdminService from '../service';
  import service from '../service';
  import { M } from '../../external';
  import Image from './Attachments/Image.svelte';
  import Stars from './Attachments/StarRating/Stars.svelte';

  export let comment, limit, onDeleteComment, allowReplies;

  let headerComponent;

  let hasRepliesFetched = comment.children && comment.children.length > 0;
  let clickedReply = false;

  const TRUNCATE_LENGTH = 300;
  let isTruncated = false;
  let showFullComment = false;
  const truncateIfLong = (text, showFullComment) => {
    if (!text) {
      return '';
    }

    if (showFullComment) {
      return text;
    }
    if (text.length > TRUNCATE_LENGTH) {
      isTruncated = true;
      return text.substring(0, TRUNCATE_LENGTH) + '...';
    }
    return text;
  };

  let isChildrenCollapsed = false;
  const onCollapseChildren = () => {
    isChildrenCollapsed = true;
  };

  const onExpandChildren = () => {
    isChildrenCollapsed = false;
  };

  const childrenPagination = {
    hasNext: null,
    nextTimestamp: null
  };

  const onShowReplies = async () => {
    const response = await CommentAdminService.getAllCommentsByParent(comment.id, null, limit, 'asc');
    response.comments = response.comments || [];

    hasRepliesFetched = true;
    childrenPagination.hasNext = response.hasNext;
    childrenPagination.nextTimestamp = response.nextTimestamp;
    comment.children = response.comments;
  };
  const onShowMore = async () => {
    const response = await CommentAdminService.getAllCommentsByParent(
      comment.id,
      childrenPagination.nextTimestamp,
      limit,
      'asc'
    );
    response.comments = response.comments || [];

    childrenPagination.hasNext = response.hasNext;
    childrenPagination.nextTimestamp = response.nextTimestamp;

    // Replies are always sorted in ascending order
    comment.children = [...comment.children, ...response.comments];
    newlyPosted.checkAndRemoveIfTheyHaveFetched(comment.children);
  };

  const newlyPosted = {
    comments: [],
    checkAndRemoveIfTheyHaveFetched: (comments) => {
      // check if newly posted comments are already fetched and remove them using filter
      newlyPosted.comments = newlyPosted.comments.filter((newlyPostedComment) => {
        return !comments.some((comment) => {
          return comment.id === newlyPostedComment.id;
        });
      });
    }
  };

  const onSendReply = async (input, attachments) => {
    const newComment = await CommentAdminService.createCommentByParent(comment.id, input, attachments);

    if (!comment.children) {
      comment.children = [];
    }

    if (comment.children.length === 0) {
      hasRepliesFetched = true;
    }

    if (comment.children.length < comment.replies) {
      // if there are more comments to fetch
      newlyPosted.comments = [...newlyPosted.comments, newComment];
    } else {
      comment.children = [...comment.children, newComment];
    }
    clickedReply = false;
    comment.replies = comment.replies + 1;
  };

  const onEditComment = () => {
    isEditing = true;
  };

  const onDeleteReply = async (_comment, isNewlyPosted) => {
    const isDeleted = await CommentAdminService.deleteComment(_comment.id);
    // search _comment by `id` in comment.children and remove
    if (isDeleted) {
      if (isNewlyPosted) {
        newlyPosted.comments = newlyPosted.comments.filter((reply) => reply.id !== _comment.id);
      } else {
        comment.children = comment.children.filter((reply) => reply.id !== _comment.id);
      }

      if (comment.children.length === 0 && newlyPosted.comments.length === 0) {
        hasRepliesFetched = false;
      }

      comment.replies = comment.replies - 1;
    }
  };

  const onApproveComment = async () => {
    const isApproved = await CommentAdminService.approveComment(comment.id);
    if (isApproved) {
      comment.approved = true;
    }
  };

  let isEditing = false;

  const onCommentEdited = (input, attachments) => {
    service.updateComment(comment.id, input, attachments);
    comment.content = input;
    comment.attachments = attachments;
    isEditing = false;
  };
</script>

<div class:with-replies={hasRepliesFetched} class="circle-post-comment f-left col20">
  <Header
    bind:this={headerComponent}
    {comment}
    {service}
    {hasRepliesFetched}
    onCollapse={onCollapseChildren}
    onExpand={onExpandChildren}
    {onEditComment}
    {onDeleteComment}
    {onApproveComment} />
  <div class="col20 f-left circle-thread-content">
    {#if !isEditing}
      <div class="circle-comment-content col20 f-left">
        {#if comment.attachments?.starRating}
          <div class="f-left col20 mt-15 mb-15">
            <Stars readOnly={true} starClass="stars-small" defaultScore={comment.attachments.starRating.rating} />
          </div>
        {/if}
        <span class="col20 f-left circle-post-content-paragraph ql-editor">{@html comment.content || ''}</span>
        {#if isTruncated && !showFullComment}
          <div class="col20 f-left mb-5 mt-5 t-left post-more-comments-wrapper no-line">
            <a
              on:click={() => {
                showFullComment = true;
              }}
              class="comments-show-all circle-effect">{M.SHOW_MORE}</a>
          </div>
        {/if}
        {#if comment.attachments?.image}
          <Image imageDetails={comment.attachments.image} showDelete={false} />
        {/if}
        <Footer
          {comment}
          {service}
          {hasRepliesFetched}
          {onShowReplies}
          onClickReply={() => (clickedReply = true)}
          {allowReplies}
          {isChildrenCollapsed}
          toggleChildren={() => {
            headerComponent.toggle();
          }} />
      </div>
    {:else}
      <div class="circle-comment-content col20 f-left">
        <Input
          {service}
          author={service.getCurrentAuthor()}
          input={comment.content}
          attachments={comment.attachments}
          onSend={onCommentEdited}
          allowImages={true} />
      </div>
    {/if}

    <!-- Comment Replies-->
    {#if hasRepliesFetched && !isChildrenCollapsed}
      <div transition:slide class="circle-comments-reply-wrapper col20 f-left">
        {#each comment.children as reply (reply.id)}
          <svelte:self
            comment={reply}
            {limit}
            onDeleteComment={(_comment) => onDeleteReply(_comment, false)}
            allowReplies={true} />
        {/each}

        {#if newlyPosted.comments.length > 0}
          <div class="col20 f-left mb-15 t-center">
            <a on:click={onShowMore} class="comments-show-all circle-effect">{M.SHOW_MORE_COMMENTS}</a>
          </div>
          {#each newlyPosted.comments as reply (reply.id)}
            <svelte:self
              comment={reply}
              {limit}
              onDeleteComment={(_comment) => onDeleteReply(_comment, true)}
              disableReplies={true} />
          {/each}
        {/if}

        {#if newlyPosted.comments.length === 0 && comment.replies > 0 && comment.children.length < comment.replies}
          <div class="col20 f-left mb-15 t-center">
            <a on:click={onShowMore} class="comments-show-all circle-effect">{M.SHOW_MORE_COMMENTS}</a>
          </div>
        {/if}
      </div>
    {/if}
    <!-- Comment Replies-->
    {#if clickedReply}
      <Input {service} author={service.getCurrentAuthor()} focused={true} onSend={onSendReply} allowImages={true} />
    {/if}
  </div>
</div>
